import type { TickCtx, GameState } from '../tickTypes'
import { Dec } from '../../shared/decimal'

// updateSmall section 1: lastUpdate + gold/second + gold/click + fire/second
// + fire gold multiplier. Oracle: script.js:1303-1305, :1313-1352,
// :1354-1380, :1387-1413, :1416-1419.
export function goldFire(game: GameState, ctx: TickCtx): void {
  // script.js:1303-1305 — the oracle computes `diff = (Date.now() -
  // game.lastUpdate) / 1000` here, but nothing in updateSmall ever reads it:
  // DEAD code (production runs on updateLarge's own delta). Only the
  // lastUpdate write is live state.
  game.lastUpdate = ctx.now()

  // script.js:1313-1352 — gold/second formula and multipliers
  game.goldPerSecond = game.miners.mul(game.fire.div(10).add(1).log10().mul(2).add(1))
  if (game.unlocks >= 2) {
    if (game.darkMagicUpgradesBought[5]) { game.goldPerSecond = game.goldPerSecond.mul(new Dec(5).pow(game.fireUpgrade2Bought.pow(0.8))) }
    else if (game.magicUpgradesBought[8]) { game.goldPerSecond = game.goldPerSecond.mul(new Dec(1.6).pow(game.fireUpgrade2Bought.pow(0.8))) }
    else { game.goldPerSecond = game.goldPerSecond.mul(new Dec(1.25).pow(game.fireUpgrade2Bought.pow(0.8))) }
    if (game.platinumUpgradesBought[8] > 0) { game.goldPerSecond = game.goldPerSecond.mul(game.fireUpgrade4Bought.pow(game.miners.pow(0.3)).pow(game.platinumUpgradesBought[8] / 4).add(1)) }
    else { game.goldPerSecond = game.goldPerSecond.mul(game.fireUpgrade4Bought.pow(1.5).mul(game.miners).div(50).add(1)) }
  }
  if (game.unlocks >= 3) game.goldPerSecond = game.goldPerSecond.mul(1 + game.platinumUpgradesBought[0] * 0.2)
  // script.js:1322 QUIRK — reads the bare global platinumUpgrade6Effect
  // BEFORE this tick recomputes it (one-tick lag). After a fresh page load
  // the bare read resolves to the #platinumUpgrade6Effect DOM element via
  // named-element access; Decimal.mul(element) === 0, zeroing goldPerSecond
  // for one tick. Both behaviors are preserved via the promoted transient.
  if (game.platinumUpgradesBought[5] > 0) game.goldPerSecond = game.goldPerSecond.mul(ctx.transients.platinumUpgrade6Effect.value as any)
  if (game.challengesActive && game.selectedChallenges[3]) game.goldPerSecond = game.goldPerSecond.div(1e25)
  if (game.unlocks >= 4) game.goldPerSecond = game.goldPerSecond.mul(game.magicEffect)
  if (game.magicUpgradesBought[0]) game.goldPerSecond = game.goldPerSecond.mul(game.gold.add(1).log10().add(1))
  if (game.magicUpgradesBought[2]) game.goldPerSecond = game.goldPerSecond.mul(55.5)
  if (game.magicUpgradesBought[5]) {
    if (game.magicUpgradesBought[9]) { game.goldPerSecond = game.goldPerSecond.mul(game.miners.pow(3).add(1)) }
    else { game.goldPerSecond = game.goldPerSecond.mul(game.miners.pow(1.5).add(1)) }
  }
  // script.js:1332-1335 — the 4-branch ENGINE form of the magifold effect
  // ladder, kept inline (formulas.magifolds is the magifold VALUE, not this
  // ladder; the ladder's copies diverge and stay at their sites).
  if (game.unlocks >= 5) {
    if (game.violetSigilUpgradesBought[2].eq(1)) { game.goldPerSecond = game.goldPerSecond.mul(game.magifolds.pow(game.magifolds.add(10).log10().log10().add(8))) }
    else if (game.darkMagicUpgradesBought[3]) { game.goldPerSecond = game.goldPerSecond.mul(game.magifolds.pow(8)) }
    else if (game.magicUpgradesBought[18]) { game.goldPerSecond = game.goldPerSecond.mul(game.magifolds.pow(6)) }
    else { game.goldPerSecond = game.goldPerSecond.mul(game.magifolds.pow(4)) }
  }
  if (game.dragonStage >= 5) game.goldPerSecond = game.goldPerSecond.pow(game.dragonTimeEffect)
  if (game.unlocks >= 10) {
    if (!game.inHell || game.currentHellLayer < 2) game.goldPerSecond = game.goldPerSecond.pow(new Dec(1.2).pow(game.cyanSigilUpgradesBought[2].pow(0.4)))
  }
  if (game.tomeUpgradesBought[3]) game.goldPerSecond = game.goldPerSecond.pow(game.platinum.add(1e10).slog().div(2))
  if (game.holyDodecahedronUpgradesBought[4]) game.goldPerSecond = game.goldPerSecond.pow(game.redSigils.pow(0.2).add(1))
  if (game.challengesActive && game.selectedChallenges[1]) game.goldPerSecond = game.goldPerSecond.pow(0.25)
  if (game.inHell) { // script.js:1345-1352
    if (game.currentHellLayer == 1) game.goldPerSecond = game.goldPerSecond.add(1).log10().pow(5)
    else if (game.currentHellLayer == 2) game.goldPerSecond = game.goldPerSecond.add(1).log10().pow(2)
    else if (game.currentHellLayer == 3) game.goldPerSecond = game.goldPerSecond.add(1).log10()
    else if (game.currentHellLayer == 4) game.goldPerSecond = game.goldPerSecond.add(1).log10().pow(0.5)
    else if (game.currentHellLayer == 5) game.goldPerSecond = game.goldPerSecond.add(1).log10().pow(0.2)
    if (game.holyOctahedronUpgradesBought[1]) game.goldPerSecond = game.goldPerSecond.mul(1e250)
    if (game.tomeUpgradesBought[10]) game.goldPerSecond = game.goldPerSecond.pow(game.blood.add(1).log10().add(1))
  }

  // script.js:1354-1380 — gold/click formula and multipliers (state only;
  // the trailing document.* displays and the buy-miner button toggle are
  // DOM-only and stay in pristine updateSmall)
  if (game.magicUpgradesBought[8]) { game.goldPerClick = game.fireUpgrade3Bought.pow(12).mul(4).add(1) }
  else { game.goldPerClick = game.fireUpgrade3Bought.pow(2.6).mul(4).add(1) }
  if (game.unlocks >= 4) game.goldPerClick = game.goldPerClick.mul(game.magicEffect)
  if (game.magicUpgradesBought[0]) game.goldPerClick = game.goldPerClick.mul(game.gold.add(1).log10().add(1))
  if (game.magicUpgradesBought[2]) game.goldPerClick = game.goldPerClick.mul(55.5)
  // script.js:1361-1364 — magifold effect ladder, inline (see above)
  if (game.unlocks >= 5) {
    if (game.violetSigilUpgradesBought[2].eq(1)) { game.goldPerClick = game.goldPerClick.mul(game.magifolds.pow(game.magifolds.add(10).log10().log10().add(8))) }
    else if (game.darkMagicUpgradesBought[3]) { game.goldPerClick = game.goldPerClick.mul(game.magifolds.pow(8)) }
    else if (game.magicUpgradesBought[18]) { game.goldPerClick = game.goldPerClick.mul(game.magifolds.pow(6)) }
    else { game.goldPerClick = game.goldPerClick.mul(game.magifolds.pow(4)) }
  }
  if (game.dragonStage >= 5) game.goldPerClick = game.goldPerClick.pow(game.dragonTimeEffect)
  if (game.challengesActive && game.selectedChallenges[1]) game.goldPerClick = game.goldPerClick.pow(0.25)
  if (game.challengesActive && game.selectedChallenges[3]) game.goldPerClick = new Dec(20)
  if (game.inHell) { // script.js:1372-1379
    if (game.currentHellLayer == 1) game.goldPerClick = game.goldPerClick.add(1).log10().pow(5)
    else if (game.currentHellLayer == 2) game.goldPerClick = game.goldPerClick.add(1).log10().pow(2)
    else if (game.currentHellLayer == 3) game.goldPerClick = game.goldPerClick.add(1).log10()
    else if (game.currentHellLayer == 4) game.goldPerClick = game.goldPerClick.add(1).log10().pow(0.5)
    else if (game.currentHellLayer == 5) game.goldPerClick = game.goldPerClick.add(1).log10().pow(0.2)
    if (game.holyOctahedronUpgradesBought[1]) game.goldPerClick = game.goldPerClick.mul(1e250)
    if (game.tomeUpgradesBought[10]) game.goldPerClick = game.goldPerClick.pow(game.blood.add(1).log10().add(1))
  }

  // script.js:1387-1413 — fire/second formula and multipliers
  if (game.voidMagicUpgradesBought[5]) { game.firePerSecond = game.gold }
  else if (game.voidMagicUpgradesBought[4]) { game.firePerSecond = game.gold.pow("1e-10") }
  else {
    if (game.magicUpgradesBought[13]) { game.firePerSecond = new Dec(3.5).pow(game.fireUpgrade1Bought.pow(0.7)) }
    else { game.firePerSecond = new Dec(2).pow(game.fireUpgrade1Bought.pow(0.6)) }
    if (game.unlocks >= 2) game.firePerSecond = game.firePerSecond.mul(game.fireUpgrade5Bought.pow(1.5).mul(game.gold.add(1).log10()).div(5).add(1))
    if (game.unlocks >= 3) game.firePerSecond = game.firePerSecond.mul(1 + game.platinumUpgradesBought[1] * 0.2)
    if (game.magicUpgradesBought[2]) game.firePerSecond = game.firePerSecond.mul(55.5)
    if (game.magicUpgradesBought[4]) game.firePerSecond = game.firePerSecond.mul(game.gold.add(1).log2().add(1))
    // dragon-stage gates: READS only
    if (game.dragonStage == 5) game.firePerSecond = game.firePerSecond.mul(1e15)
    else if (game.dragonStage == 4) game.firePerSecond = game.firePerSecond.mul(1e8)
    else if (game.dragonStage == 3) game.firePerSecond = game.firePerSecond.mul(10000)
    else if (game.dragonStage == 2) game.firePerSecond = game.firePerSecond.mul(100)
    if (game.dragonStage >= 5) game.firePerSecond = game.firePerSecond.pow(new Dec(1.3).pow(game.dragonFood))
    if (game.unlocks >= 17) game.firePerSecond = game.firePerSecond.pow(game.blueFireUpgradesBought[1].add(1).log10().div(4).add(1))
    if (game.holyTetrahedronUpgradesBought[9]) game.firePerSecond = game.firePerSecond.pow(10000)
    if (game.challengesActive && game.selectedChallenges[2]) {
      if (game.magicUpgradesBought[14]) { game.firePerSecond = game.firePerSecond.pow(0.3) }
      else { game.firePerSecond = game.firePerSecond.pow(0.1) }
    }
    if (game.inHell) { // script.js:1409-1412
      if (game.currentHellLayer == 2 || game.currentHellLayer == 3) game.firePerSecond = game.firePerSecond.add(1).log10()
      else if (game.currentHellLayer >= 4) game.firePerSecond = new Dec(0)
    }
  }

  // script.js:1416-1419 — fire gold multiplier formula and multipliers
  if (game.darkMagicUpgradesBought[5]) { game.fireGoldMultiplier = game.fire.div(10).add(1).log10().mul(2).add(1).mul(new Dec(5).pow(game.fireUpgrade2Bought.pow(0.8))) }
  else if (game.magicUpgradesBought[8]) { game.fireGoldMultiplier = game.fire.div(10).add(1).log10().mul(2).add(1).mul(new Dec(1.6).pow(game.fireUpgrade2Bought.pow(0.8))) }
  else { game.fireGoldMultiplier = game.fire.div(10).add(1).log10().mul(2).add(1).mul(new Dec(1.25).pow(game.fireUpgrade2Bought.pow(0.8))) }
}
